import { useState, useRef, useEffect, useCallback } from "react";

export type MsgType = "SMS" | "LMS" | "MMS";

const MAX_LENGTH: Record<MsgType, number> = { SMS: 70, LMS: 2000, MMS: 2000 };

const MSG_TYPE_INFO: Record<MsgType, { label: string; desc: string; color: string }> = {
  SMS: { label: "SMS", desc: "단문 (70자)", color: "from-violet-600 to-violet-500" },
  LMS: { label: "LMS", desc: "장문 (2,000자)", color: "from-pink-600 to-pink-500" },
  MMS: { label: "MMS", desc: "멀티미디어", color: "from-amber-600 to-amber-500" },
};

const TEMPLATES = [
  { label: "인증번호", text: "[Signal] 인증번호는 [123456]입니다. 3분 내에 입력해주세요." },
  { label: "예약 확인", text: "안녕하세요, [고객명]님. [날짜] [시간] 예약이 확인되었습니다. 문의: 02-0000-0000" },
  { label: "배송 안내", text: "[고객명]님, 주문하신 상품이 발송되었습니다. 운송장번호: [번호]. 배송 조회: [링크]" },
  { label: "이벤트 안내", text: "안녕하세요! 특별 이벤트를 안내드립니다. [이벤트 내용] 자세한 내용: [링크] 수신거부: 080-000-0000" },
];

interface Props {
  msgType: MsgType;
  onMsgTypeChange: (t: MsgType) => void;
  message: string;
  onMessageChange: (msg: string) => void;
  subject: string;
  onSubjectChange: (s: string) => void;
  imageFile: File | null;
  onImageChange: (f: File | null) => void;
  perMessageRate?: number | null;
}

function getUnitPrice(msgType: MsgType, perMessageRate: number | null): number {
  const base = perMessageRate ?? 20;
  if (msgType === "SMS") return base;
  if (msgType === "LMS") return Math.floor(base * 2.5);
  return Math.floor(base * 5);
}

export default function MessageComposer({
  msgType,
  onMsgTypeChange,
  message,
  onMessageChange,
  subject,
  onSubjectChange,
  imageFile,
  onImageChange,
  perMessageRate,
}: Props) {
  const [showTemplates, setShowTemplates] = useState(false);
  const [liveCount, setLiveCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const maxLen = MAX_LENGTH[msgType];
  const price = getUnitPrice(msgType, perMessageRate ?? null);

  // Track if we're programmatically updating (to avoid loops)
  const isProgrammaticUpdate = useRef(false);

  // Sync parent's message into textarea DOM when it changes externally (template select, msgType switch, etc.)
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    // Only sync if the DOM value doesn't already match
    if (ta.value !== message) {
      isProgrammaticUpdate.current = true;
      ta.value = message;
      setLiveCount(message.length);
      isProgrammaticUpdate.current = false;
    }
  }, [message]);

  // Sync liveCount on mount and when msgType/maxLen changes
  useEffect(() => {
    setLiveCount(message.length);
  }, [message.length, maxLen]);

  const handleInput = useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
    if (isProgrammaticUpdate.current) return;
    const ta = e.target as HTMLTextAreaElement;
    const raw = ta.value;
    setLiveCount(raw.length);

    if (raw.length > maxLen) {
      const truncated = raw.slice(0, maxLen);
      // Update DOM directly to avoid flicker
      ta.value = truncated;
      setLiveCount(maxLen);
      // Update parent state
      onMessageChange(truncated);
    } else {
      onMessageChange(raw);
    }
  }, [maxLen, onMessageChange]);

  const handleTypeChange = (t: MsgType) => {
    if (t === "MMS" || t === "LMS") return;
    onMsgTypeChange(t);
    if (t === "SMS" && message.length > 70) {
      onMessageChange(message.slice(0, 70));
    }
    if (t !== "MMS") {
      onImageChange(null);
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageChange(file);
      onMsgTypeChange("MMS");
    }
  };

  const imagePreviewUrl = imageFile ? URL.createObjectURL(imageFile) : null;

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-5">
        <div>
          <h3 className="text-white font-semibold text-sm md:text-base">메시지 작성</h3>
          <p className="text-gray-500 text-[10px] md:text-xs mt-0.5">발송할 내용을 입력하세요</p>
        </div>
        <button
          onClick={() => setShowTemplates(!showTemplates)}
          className={`flex items-center gap-1 md:gap-1.5 px-2.5 md:px-3 py-1.5 rounded-lg text-[10px] md:text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
            showTemplates
              ? "bg-violet-500/20 text-violet-400 border border-violet-500/30"
              : "bg-white/[0.05] text-gray-400 hover:text-white border border-white/[0.08]"
          }`}
        >
          <i className="ri-file-copy-2-line text-xs md:text-sm"></i>
          템플릿
        </button>
      </div>

      {/* Template picker */}
      {showTemplates && (
        <div className="mb-3 md:mb-4 bg-[#0d0d1a] border border-white/[0.1] rounded-xl overflow-hidden">
          <div className="px-3 md:px-4 py-2 md:py-2.5 border-b border-white/[0.07]">
            <p className="text-gray-400 text-[10px] md:text-xs font-medium">자주 쓰는 템플릿</p>
          </div>
          {TEMPLATES.map((tpl) => (
            <button
              key={tpl.label}
              onClick={() => {
                onMessageChange(tpl.text.slice(0, maxLen));
                setShowTemplates(false);
              }}
              className="w-full flex items-start gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 hover:bg-white/[0.05] cursor-pointer transition-colors text-left border-b border-white/[0.04] last:border-0"
            >
              <span className="bg-violet-500/20 text-violet-400 text-[10px] md:text-xs font-semibold px-1.5 md:px-2 py-0.5 rounded-md whitespace-nowrap shrink-0 mt-0.5">
                {tpl.label}
              </span>
              <p className="text-gray-400 text-[10px] md:text-xs leading-relaxed line-clamp-2">{tpl.text}</p>
            </button>
          ))}
        </div>
      )}

      {/* Type selector */}
      <div className="grid grid-cols-3 gap-1.5 md:gap-2 mb-4 md:mb-5">
        {(["SMS", "LMS", "MMS"] as MsgType[]).map((t) => {
          const info = MSG_TYPE_INFO[t];
          const isActive = msgType === t;
          const isDisabled = t === "MMS" || t === "LMS";
          return (
            <button
              key={t}
              onClick={() => handleTypeChange(t)}
              disabled={isDisabled}
              className={`flex flex-col items-center gap-0.5 md:gap-1 py-2 md:py-3 rounded-xl border whitespace-nowrap transition-all ${
                isDisabled
                  ? "bg-white/[0.02] border-white/[0.06] text-gray-600 cursor-not-allowed opacity-60"
                  : isActive
                    ? "bg-gradient-to-br from-violet-600/30 to-pink-600/20 border-violet-500/40 text-white cursor-pointer"
                    : "bg-white/[0.03] border-white/[0.08] text-gray-500 hover:text-gray-300 hover:border-white/[0.15] cursor-pointer"
              }`}
            >
              <span className={`text-xs md:text-sm font-bold ${isActive ? "text-white" : ""}`}>{info.label}</span>
              <span className="text-[10px] md:text-xs opacity-70">{info.desc}</span>
            </button>
          );
        })}
      </div>

      {/* Subject (LMS only) */}
      {msgType === "LMS" && (
        <div className="mb-2.5 md:mb-3">
          <label className="block text-gray-400 text-[10px] md:text-xs mb-1 md:mb-1.5 font-medium">제목 (선택)</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            placeholder="메시지 제목을 입력하세요"
            maxLength={40}
            className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 md:px-4 py-2 md:py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 transition-all"
          />
        </div>
      )}

      {/* Message textarea */}
      <div className="mb-3 md:mb-4">
        <div className="flex items-center justify-between mb-1 md:mb-1.5">
          <label className="text-gray-400 text-[10px] md:text-xs font-medium">메시지 내용</label>
          <span className={`text-[10px] md:text-xs font-medium ${liveCount > maxLen * 0.9 ? "text-amber-400" : liveCount > 0 ? "text-gray-400" : "text-gray-600"}`}>
            {Math.min(liveCount, maxLen).toLocaleString()} / {maxLen.toLocaleString()}자
          </span>
        </div>
        <textarea
          ref={textareaRef}
          defaultValue={message}
          onInput={handleInput}
          placeholder={`메시지 내용을 입력하세요\n최대 ${maxLen.toLocaleString()}자까지 입력 가능합니다`}
          rows={msgType === "SMS" ? 4 : 6}
          className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-3 md:px-4 py-2.5 md:py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 transition-all resize-none leading-relaxed"
        />
        {/* Progress bar */}
        <div className="mt-1.5 h-0.5 bg-white/[0.06] rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${
              liveCount > maxLen * 0.9
                ? "bg-amber-500"
                : "bg-gradient-to-r from-violet-500 to-pink-500"
            }`}
            style={{ width: `${Math.min((liveCount / maxLen) * 100, 100)}%` }}
          />
        </div>
      </div>

      {/* Cost info */}
      <div className="flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3 bg-white/[0.03] rounded-xl border border-white/[0.06]">
        <div className="flex items-center gap-1.5 md:gap-2">
          <div className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
            <i className="ri-coins-line text-amber-400 text-sm md:text-base"></i>
          </div>
          <span className="text-gray-400 text-[10px] md:text-xs">건당 발송 비용</span>
        </div>
        <div className="text-right">
          <span className="text-white text-xs md:text-sm font-bold">₩{price.toLocaleString()}</span>
          <span className="text-gray-500 text-[10px] md:text-xs ml-1">/ 건</span>
        </div>
      </div>
    </div>
  );
}